<template>
  <div v-if="visible" class="api-outage" role="alertdialog" aria-live="assertive">
    <div class="api-outage__card">
      <template v-if="recovered">
        <div class="api-outage__icon api-outage__icon--ok">
          <i class="ri-check-line"></i>
        </div>
        <h4 class="api-outage__title">Back online</h4>
        <p class="api-outage__text">
          The server is responding again. If the update changed this app, reload to pick
          up the latest version.
        </p>
        <div class="api-outage__actions">
          <button type="button" class="btn btn-success btn-sm" @click="reload">
            <i class="ri-refresh-line align-bottom me-1"></i> Reload page
          </button>
          <button type="button" class="btn btn-light btn-sm" @click="dismiss">
            Continue without reloading
          </button>
        </div>
      </template>

      <template v-else>
        <div class="api-outage__icon">
          <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
        </div>
        <h4 class="api-outage__title">{{ title }}</h4>
        <p class="api-outage__text">{{ message }}</p>
        <p class="api-outage__meta">
          Reconnecting automatically — {{ elapsedLabel }}
          <span class="api-outage__code" data-testid="outage-code">· {{ noticeCode }}</span>
        </p>
        <!-- Something to do while the release finishes — Chrome's offline dinosaur, but
             snake. Opt-in: it waits for Space / tap, so the notice above is read first.
             Project Settings → Settings → Platform can switch it off for the deployment. -->
        <SnakeGame v-if="showGame" />
        <div class="api-outage__actions">
          <button type="button" class="btn btn-primary btn-sm" @click="retry">
            <i class="ri-restart-line align-bottom me-1"></i> Retry now
          </button>
        </div>
        <p class="api-outage__note">
          Anything you have typed on this page is still here — it will save once the
          connection is back.
        </p>
      </template>
    </div>
  </div>
</template>

<script>
/**
 * Full-screen notice shown while the API is unreachable — most often because a release
 * is mid-flight and the API site's app pool is stopped while the SPA site keeps serving.
 *
 * Mounted ONCE from App.vue, outside <keep-alive>, so it survives navigation and cannot
 * appear per open workspace tab. It renders nothing at all unless
 * helpers/apiStatus.js has CONFIRMED the outage with a failed /health probe — see that
 * file for why a single failed request is never enough.
 */
import {
  apiStatus,
  apiOutageReason,
  apiOutageSince,
  outageShowGame,
  refreshOutagePanelConfig,
  retryNow,
  dismissRecovered,
  reloadApp,
} from "@/helpers/apiStatus";
import { outageNoticeCode } from "@/helpers/apiAvailability.mjs";
import SnakeGame from "@/components/SnakeGame.vue";

export default {
  name: "ApiOfflineOverlay",
  components: { SnakeGame },
  data() {
    return { now: Date.now(), ticker: null };
  },
  computed: {
    status() {
      return apiStatus.value;
    },
    visible() {
      return this.status === "offline" || this.status === "recovered";
    },
    recovered() {
      return this.status === "recovered";
    },
    reason() {
      return apiOutageReason.value;
    },
    // Deliberately worded without "error", "failed" or "API": an operator reads this out to
    // support, and the notice code beside the timer is what tells the developer which case
    // it is (UPD-1 normal deploy, UPD-2 the API answers but is broken — deploy/README.md).
    title() {
      if (this.reason === "network") return "No internet connection";
      if (this.reason === "errored") return "Update is taking longer than expected";
      return "Update in progress";
    },
    message() {
      if (this.reason === "network") {
        return "This device is offline. The app will continue as soon as the connection returns.";
      }
      if (this.reason === "errored") {
        return "Some final steps of the update are still running, and the app keeps checking on its own. "
          + "If this notice is still here after a few minutes, tell your support team the code shown below.";
      }
      return "The server is being updated and is briefly unavailable. This usually takes under a minute.";
    },
    noticeCode() {
      return outageNoticeCode(this.reason);
    },
    showGame() {
      return outageShowGame.value;
    },
    elapsedLabel() {
      const since = apiOutageSince.value;
      if (!since) return "just now";
      const secs = Math.max(0, Math.round((this.now - since) / 1000));
      if (secs < 60) return `${secs}s`;
      const mins = Math.floor(secs / 60);
      return `${mins}m ${secs % 60}s`;
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(on) {
        // Only tick while something is on screen — a 1s interval running for the whole
        // session to update a counter nobody can see is pure waste.
        if (on && !this.ticker) {
          this.now = Date.now();
          this.ticker = setInterval(() => { this.now = Date.now(); }, 1000);
        } else if (!on && this.ticker) {
          clearInterval(this.ticker);
          this.ticker = null;
        }
      },
    },
  },
  mounted() {
    // Mounted once at app start, while the API is (normally) up — the moment to learn
    // whether the next outage should offer the game.
    refreshOutagePanelConfig();
  },
  beforeUnmount() {
    if (this.ticker) clearInterval(this.ticker);
  },
  methods: {
    retry: retryNow,
    dismiss: dismissRecovered,
    reload: reloadApp,
  },
};
</script>

<style scoped>
.api-outage {
  position: fixed;
  inset: 0;
  /* Above every Bootstrap layer (modal 1055, offcanvas 1045, tooltip 1080) and above
     the grid-maximize overlay, so the notice is never hidden behind an open dialog. */
  z-index: 20000;
  display: flex;
  justify-content: center;
  padding: 1rem;
  /* The card centres through its own auto margins (not align-items), so when it is
     taller than a short or landscape phone screen it scrolls instead of losing its top. */
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
}

.api-outage__card {
  width: 100%;
  /* 28rem, not 26: room for the 320px game board plus the card's own padding. */
  max-width: 28rem;
  margin: auto;
  padding: 1.75rem 1.5rem;
  border-radius: 0.75rem;
  background: var(--vz-card-bg, #fff);
  color: var(--vz-body-color, #212529);
  box-shadow: 0 1.5rem 3rem rgba(0, 0, 0, 0.3);
  text-align: center;
}

.api-outage__icon {
  margin-bottom: 0.75rem;
  font-size: 2rem;
  line-height: 1;
}

.api-outage__icon--ok {
  color: var(--vz-success, #0ab39c);
}

.api-outage__title {
  margin-bottom: 0.5rem;
  font-size: 1.125rem;
}

.api-outage__text {
  margin-bottom: 0.75rem;
  color: var(--vz-text-muted, #878a99);
}

.api-outage__meta {
  margin-bottom: 1rem;
  font-size: 0.8125rem;
  color: var(--vz-text-muted, #878a99);
}

.api-outage__code {
  white-space: nowrap;
  letter-spacing: 0.02em;
}

.api-outage__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
}

.api-outage__note {
  margin: 1rem 0 0;
  font-size: 0.75rem;
  color: var(--vz-text-muted, #878a99);
}
</style>
